import type { JourneyRuntime, StageRuntime } from '@cyberscore/contracts';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  FileText,
  Gauge,
  Landmark,
  ListChecks,
  MessagesSquare,
  Send,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { can, useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { useIsBusiness, useOwnsBusiness } from '../business/context';
import { ConnectionsCard } from '../connections/ConnectionsCard';
import { useDocuments, useSuggestions } from '../documents/api';
import { reviewGroups } from '../documents/files';
import {
  useAddParticipant,
  useAssessment,
  useCanStart,
  useCandidates,
  useJourney,
  useQuestions,
} from './api';
import { STATUS_LABEL, industryLabel } from './AssessmentsPage';
import { useFinish } from './finish';
import { STATUS_TONE } from './labels';
import { ChangesForYou } from '../review/ChangesForYou';
import { FollowUpsForYou } from '../follow-ups/FollowUpsForYou';
import { CarriedCard } from '../campaigns/CarriedCard';
import { ConflictsCard } from '../conflicts/ConflictsPage';
import { MessagesLink } from '../messages/MessagesLink';
import { Conditions } from '../decisions/Conditions';
import { useChangeRequests, useReviewAccess } from '../review/api';
import { counts, isAskable, nextQuestion, remaining, summarize, type QuestionRow } from './flow';
import { PathCard } from './PathCard';
import { StageIcon, stageNote, STAGE_LABEL } from './stages';

/** One assessment: where it stands in its journey, every answer so far, and who is taking part. */
export function AssessmentPage({ id }: { id: string }) {
  const detail = useAssessment(id);
  const qs = useQuestions(id);
  const journey = useJourney(id, !!detail.data?.journey_key);
  // The configuration version is for staff; the business sees its answers.
  const business = useIsBusiness();
  const own = useOwnsBusiness();
  const reviewer = useReviewAccess();
  // Insurer and broker staff place the client with insurers (task 93).
  const { tenant } = useSession();
  const insurers = !business && tenant?.kind !== 'platform' && can(tenant, 'assessments.view');
  const remediation = detail.data?.status === 'in_remediation';
  const changes = useChangeRequests(id, remediation);

  if (detail.isPending || qs.isPending) return <div className="empty">Loading…</div>;
  if (detail.isError || qs.isError)
    return (
      <div className="empty">
        <strong>Could not load this assessment</strong>
        {(detail.error ?? qs.error)?.message}
      </div>
    );

  const a = detail.data;
  const rows = qs.data.questions;
  const next = nextQuestion(rows, undefined, qs.data.queue);
  const answered = rows.filter(counts).length;
  const left = remaining(rows);
  const done = qs.data.journey ? qs.data.journey.state === 'completed' : left.mandatory === 0;
  // Which answers can change now: any while answering; in remediation, only those the insurer asked about.
  const asked = new Set(
    (changes.data?.requests ?? []).filter((r) => r.state === 'sent').map((r) => r.question_key),
  );
  const canChange = (key: string) => a.status === 'in_progress' || (remediation && asked.has(key));

  return (
    <>
      <div className="crumbs">
        <a href={href.assessments()}>Assessments</a>
        <ChevronRight size={14} />
        {a.title}
      </div>
      <div className="page-head row-between">
        <div>
          <h1>{a.customer_name}</h1>
          <p>
            {a.environment && <>IT environment <b>{a.environment}</b> · </>}
            {a.title} · {industryLabel(a.context.industry)}
            {a.context.country && ` · ${a.context.country}`} ·{' '}
            <span className={`pill ${STATUS_TONE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
          </p>
        </div>
        <div className="row gap">
          <a className="btn" href={href.score(id)}>
            <Gauge size={16} /> CyberScore
          </a>
          <MessagesLink id={id} />
          {insurers && (
            <a className="btn" href={href.insurers(id)}>
              <Landmark size={16} /> Insurers
            </a>
          )}
          {reviewer.read && a.status !== 'in_progress' && (
            <a className="btn btn-primary" href={href.review(id)}>
              <ListChecks size={16} /> {reviewer.write && (a.status === 'submitted' || a.status === 'in_review') ? 'Review answers' : 'Review'}
            </a>
          )}
        {a.status === 'in_progress' && next && (
          <div className="row gap">
            <a className="btn" href={href.chat(id)}>
              <MessagesSquare size={16} /> Answer in chat
            </a>
            <a className="btn btn-primary" href={href.answer(id)}>
              {answered ? 'Continue' : 'Start'} <ArrowRight size={16} />
            </a>
          </div>
        )}
        </div>
      </div>

      <div className="layout-side">
        <div className="stack">
          <ChangesForYou id={id} status={a.status} />
          {!reviewer.read && <FollowUpsForYou id={id} status={a.status} />}
          <CarriedCard assessmentId={id} canAnswer={a.status === 'in_progress'} />
          <ConflictsCard assessmentId={id} canAnswer={a.status === 'in_progress'} />
          {a.status === 'decided' && <Conditions id={id} mode={reviewer.read ? 'staff' : 'business'} />}
          <section className="card summary">
            <div className="summary-stats">
              <div>
                <b>{answered}</b>
                <span>answered</span>
              </div>
              <div>
                <b>{left.mandatory}</b>
                <span>required to go</span>
              </div>
              <div>
                <b>{left.minutes ? `~${left.minutes} min` : '—'}</b>
                <span>left</span>
              </div>
            </div>
            {a.status === 'in_progress' ? (
              done && <Submit id={id} />
            ) : (
              a.submitted_at && !remediation && (
                <div className="feedback" role="status">
                  {own ? 'Finished' : 'Submitted'} on {new Date(a.submitted_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}.
                  Answers can no longer be changed.
                  {own && <> <a className="link" href={href.report()}>See your report</a></>}
                </div>
              )
            )}
          </section>

          {journey.data ? (
            journey.data.stages.map((s) => (
              <StageCard key={s.key} id={id} stage={s} rows={rows.filter((r) => r.stage?.key === s.key)} journey={journey.data} canChange={canChange} />
            ))
          ) : (
            <section className="card">
              <QuestionList id={id} rows={rows.filter((r) => r.state.state !== 'hidden')} canChange={canChange} />
            </section>
          )}
        </div>

        <aside className="stack">
          <PathCard id={id} enabled={!!qs.data.journey} />
          <Documents id={id} />
          <ConnectionsCard id={id} />
          <Participants id={id} people={a.participants} />
          {!business && (
          <section className="card pad">
            <div className="eyebrow">Configuration</div>
            <p className="muted small">
              Platform version {qs.data.bundle.platform_version}
              {qs.data.bundle.tenant_version !== null && `, your version ${qs.data.bundle.tenant_version}`}. Fixed
              when the assessment started, so later changes do not move it.
            </p>
            <div className="mono small clip" title={qs.data.bundle.content_sha256}>
              {qs.data.bundle.content_sha256.slice(0, 16)}…
            </div>
          </section>
          )}
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

/** How many documents there are, and what they say that still needs a person's decision. */
function Documents({ id }: { id: string }) {
  const docs = useDocuments(id);
  const suggestions = useSuggestions(id);
  const g = reviewGroups(suggestions.data ?? []);
  const n = docs.data?.length ?? 0;
  return (
    <section className="card pad">
      <div className="row-between">
        <div className="eyebrow">Documents</div>
        <a className="btn btn-sm btn-ghost" href={href.documents(id)}>
          <FileText size={14} /> {n ? 'Open' : 'Upload'}
        </a>
      </div>
      <p className="muted small">
        {docs.isPending
          ? 'Loading…'
          : n
            ? `${n} ${n === 1 ? 'document' : 'documents'} uploaded.`
            : 'Upload a policy schedule or a backup report and we’ll read the answers it gives.'}
      </p>
      {g.total > 0 && (
        <a className={`notice ${g.conflicts.length ? '' : 'neutral'} block-link`} href={href.documents(id)}>
          {g.total} {g.total === 1 ? 'answer' : 'answers'} from your documents to review
          {g.conflicts.length > 0 && `, ${g.conflicts.length} where they disagree`}
        </a>
      )}
    </section>
  );
}

/**
 * Everything required is answered. A business on its own finishes it in one click and goes to its report
 * (decision 0034, task 105). Handing it to an insurer takes one confirming click.
 */
function Submit({ id }: { id: string }) {
  const { own, canSubmit, run, pending } = useFinish(id);
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="feedback submit-row">
      <div className="grow">
        <b>Everything required is answered.</b>{' '}
        {confirming
          ? 'Submit now? Answers can’t be changed after this.'
          : canSubmit
            ? own ? 'Optional questions can still sharpen the result. Finishing fixes these answers; you can start a new check any time.' : 'Optional questions can still sharpen the result, or submit it to your insurer now.'
            : 'Optional questions can still sharpen the result. The business owner submits it.'}
      </div>
      {canSubmit && (
        <div className="row gap">
          {confirming && (
            <button type="button" className="btn" disabled={pending} onClick={() => setConfirming(false)}>
              Not yet
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={pending}
            onClick={async () => {
              if (!own && !confirming) return setConfirming(true);
              await run();
              setConfirming(false);
            }}
          >
            <Send size={15} /> {pending ? (own ? 'Finishing…' : 'Submitting…') : own ? 'Finish and get my report' : confirming ? 'Submit now' : 'Submit to insurer'}
          </button>
        </div>
      )}
    </div>
  ); // prettier-ignore
}

function StageCard({
  id,
  stage,
  rows,
  journey,
  canChange,
}: {
  id: string;
  stage: StageRuntime;
  rows: QuestionRow[];
  journey: JourneyRuntime;
  canChange: (key: string) => boolean;
}) {
  // prettier-ignore
  const [open, setOpen] = useState(stage.state === 'active' || stage.state === 'available');
  const shown = rows.filter((r) => r.state.state !== 'hidden');
  const note = stageNote(stage, journey);
  return (
    <section className={`card stage-card ${stage.state}`}>
      <button type="button" className="stage-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <StageIcon state={stage.state} />
        <div className="grow">
          <div className="stage-name">
            {stage.key} · {stage.name}
            {!stage.mandatory && <span className="badge">Optional</span>}
          </div>
          <div className="muted small">
            {stage.state === 'locked' || stage.state === 'skipped'
              ? note
              : `${stage.answered} of ${stage.asked} answered${stage.open_mandatory.length ? ` · ${stage.open_mandatory.length} required to go` : ''}`}
          </div>
        </div>
        <span className={`pill ${stage.state === 'completed' ? 'ok' : 'neutral'}`}>{STAGE_LABEL[stage.state]}</span>
        <ChevronDown size={16} className={open ? 'flip' : ''} />
      </button>
      {open && stage.state !== 'skipped' && <QuestionList id={id} rows={shown} canChange={canChange} />}
    </section>
  ); // prettier-ignore
}

function QuestionList({
  id,
  rows,
  canChange,
}: {
  id: string;
  rows: QuestionRow[];
  canChange: (key: string) => boolean;
}) {
  if (!rows.length) return <div className="empty compact">No questions to show here yet.</div>;
  return (
    <ol className="qlist">
      {rows.map((r) => {
        const can = isAskable(r) && canChange(r.key);
        return (
          <li key={r.key}>
            <div className="grow">
              <div className="qtext">
                {r.definition.text}
                {r.definition.mandatory && (
                  <span className="req" title="Required">
                    {' '}
                    *
                  </span>
                )}
              </div>
              <div className={r.answer && r.state.state !== 'implied' ? 'answer' : 'muted small'}>
                {r.state.state === 'implied'
                  ? `Not in place: ${r.state.by.label}`
                  : r.answer
                    ? summarize(r.definition, r.answer.value)
                    : r.state.state === 'waiting'
                      ? `After ${r.state.on.join(', ')}`
                      : r.state.state === 'visible' && !r.state.ask
                        ? 'Filled in from your systems'
                        : 'Not answered'}
              </div>
            </div>
            {can && (
              <a className="btn btn-sm" href={href.answer(id, r.key)}>
                {r.answer ? 'Change' : 'Answer'}
              </a>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Participants({
  id,
  people,
}: {
  id: string;
  people: { user_id: string; display_name: string | null; email: string | null }[];
}) {
  // prettier-ignore
  const canAdd = useCanStart();
  const [adding, setAdding] = useState(false);
  const candidates = useCandidates(id, canAdd && adding);
  const add = useAddParticipant(id);
  const toast = useToast();
  const [pick, setPick] = useState('');
  return (
    <section className="card pad">
      <div className="row-between">
        <div className="eyebrow">Taking part</div>
        {canAdd && !adding && (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAdding(true)}>
            <UserPlus size={14} /> Add
          </button>
        )}
      </div>
      {people.length ? (
        <ul className="people">
          {people.map((p) => (
            <li key={p.user_id}>
              <span>{p.display_name ?? p.email}</span>
              {p.display_name && p.email && <span className="muted small">{p.email}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">Only staff can see this assessment until someone is added.</p>
      )}
      {adding && (
        <form
          className="input-row mt"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!pick) return;
            try {
              await add.mutateAsync(pick);
              setPick('');
              setAdding(false);
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not add them', 'bad');
            }
          }}
        >
          <select className="input" aria-label="Person to add" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">{candidates.isPending ? 'Loading…' : candidates.data?.length ? 'Choose a person' : 'No one else to add'}</option>
            {candidates.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name ?? c.email}
              </option>
            ))}
          </select>
          <button type="submit" className="btn" disabled={!pick || add.isPending}>
            Add
          </button>
        </form>
      )}
    </section>
  ); // prettier-ignore
}
